import { createContext, useCallback, useEffect, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';

import { FolderPagesGuard } from '../../components/Access/FolderPagesGuard';
import { LoadingFailure } from '../../components/LoadingFailure';
import { getLoadingFailureMessage } from '../../utils/loading-failure-message.util';
import { useIdParam } from '../../utils/use-id-param.util';
import { useFindOneFolderQuery } from '../../pages/Folder/hooks/useFindOneFolderQuery.hook';

import { useNavigationContext } from '../NavigationContext/hooks/useNavigationContext.hook';
import { useSpaceEmitterColor } from '../UiSettingsContext/hooks/useSpaceEmitterColor.hook';
import { SpaceLevel } from '../UiSettingsContext/enums/space-level.enum';
import type { Page } from '../NavigationContext/enums/page.enum';

import { FolderContextStatus } from './types/folder-context.type';
import type { FolderContextData } from './types/folder-context.type';

export { DummyFolderContextProvider } from './DummyFolderContextProvider';

const FolderContext = createContext<FolderContextData | null>(null);

interface Props {
  readonly page: Page;
  readonly children?: ReactNode;
}

const FolderContextProvider = ({
  page,
  children,
}: Readonly<Props>): JSX.Element => {
  const { setPage, setFolderSpaceInfo } = useNavigationContext();

  useEffect(() => {
    setPage(page);
  }, [setPage, page]);

  const folderId = useIdParam('folderId');
  const {
    data: folderQueryData,
    isLoading,
    isError,
    error,
    refetch,
  } = useFindOneFolderQuery(folderId);
  const folder = folderQueryData?.findOneFolder;

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const failureMessage = getLoadingFailureMessage({
    isLoading,
    isError,
    error,
    isFound: !!folder,
    subject: 'le dossier',
    notFoundMessage: 'Ce dossier est introuvable.',
  });

  useEffect(() => {
    if (!folder) {
      return;
    }

    setFolderSpaceInfo(folder);
  }, [folder, setFolderSpaceInfo]);

  useSpaceEmitterColor(SpaceLevel.Folder, folder?.consolidation);

  const contextValue = useMemo<FolderContextData>(() => {
    if (isLoading) {
      return {
        status: FolderContextStatus.Loading,
        folderId,
        folder: undefined,
      };
    }

    if (isError || !folder) {
      return {
        status: FolderContextStatus.Error,
        folderId,
        folder: undefined,
      };
    }

    return {
      status: FolderContextStatus.Ready,
      folderId,
      folder,
    };
  }, [folderId, folder, isLoading, isError]);

  return (
    <FolderContext.Provider value={contextValue}>
      {failureMessage ? (
        <LoadingFailure failure={failureMessage} onRetry={retry} />
      ) : (
        (children ?? <FolderPagesGuard />)
      )}
    </FolderContext.Provider>
  );
};

export { FolderContext, FolderContextProvider };
